/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 29/11/2025 - 20:06:58
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 29/11/2025
    * - Author          : USER
    * - Modification    : 
**/
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import LoadingSpinner from '../ui/LoadingSpinner';
import api, { setClerkToken, clearClerkToken } from '../../services/api';

/**
 * RequireCompleteProfile
 * - Ensures user is signed in with Clerk
 * - Verifies that a backend profile exists for the Clerk user
 * - Redirects to /complete-profile when backend profile is missing
 * - FIXED: Prevents infinite loops by using refs and proper state management
 */
const RequireCompleteProfile = ({ children }) => {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);

  // Use refs to prevent infinite loops
  const hasCheckedRef = useRef(false);
  const lastUserIdRef = useRef(null);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    // Only run check if Clerk is loaded and we have a user
    if (!isLoaded) return;

    // Prevent duplicate checks for the same user
    if (hasCheckedRef.current && lastUserIdRef.current === user?.id) {
      setChecking(false);
      return;
    }

    // Prevent concurrent checks
    if (isCheckingRef.current) return;

    const checkBackendProfile = async () => {
      // Not signed in -> send to login
      if (!user) {
        console.log('RequireCompleteProfile: No user, redirecting to login');
        navigate('/login', { replace: true });
        return;
      }

      // Mark as checking to prevent concurrent calls
      isCheckingRef.current = true;
      setChecking(true);
      setError(null);

      try {
        console.log('RequireCompleteProfile: Checking backend profile for user:', user.id);

        const token = await getToken();

        if (!token) {
          throw new Error('No authentication token available');
        }

        // Keep the API layer in sync
        setClerkToken(token);

        const response = await api.get('/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('RequireCompleteProfile: Profile check response:', response);

        // If backend returns a user with registrationType, profile is complete
        if (response?.user && response.user.registrationType) {
          console.log('RequireCompleteProfile: Profile complete, allowing access');
          hasCheckedRef.current = true;
          lastUserIdRef.current = user.id;
          setChecking(false);
          return;
        }

        // If backend explicitly indicates profile missing, send user to completion
        if (response?.needsProfileCompletion || response?.message?.toLowerCase()?.includes('profile')) {
          console.log('RequireCompleteProfile: Profile incomplete, redirecting to complete-profile');
          navigate('/complete-profile', { replace: true });
          return;
        }

        // Otherwise, assume profile not complete and redirect
        console.log('RequireCompleteProfile: Profile not complete, redirecting to complete-profile');
        navigate('/complete-profile', { replace: true });

      } catch (err) {
        const status = err?.response?.status;
        const errorMsg = err?.message || 'Profile verification failed';
        console.error('RequireCompleteProfile: Profile check failed:', errorMsg);

        // If 404 or "Resource not found" - profile doesn't exist yet, redirect to complete it
        if (status === 404 || errorMsg.includes('not found') || errorMsg.includes('Resource not found')) {
          console.log('RequireCompleteProfile: Profile not found (404), redirecting to complete-profile');
          navigate('/complete-profile', { replace: true });
          return;
        }

        // If 401 - try to refresh token once
        if (status === 401) {
          try {
            console.log('RequireCompleteProfile: Token expired, attempting refresh');
            clearClerkToken();
            const freshToken = await getToken();
            if (freshToken) {
              setClerkToken(freshToken);
              // Retry once with fresh token
              const retryResponse = await api.get('/auth/profile', {
                headers: { Authorization: `Bearer ${freshToken}` }
              });

              if (retryResponse?.user && retryResponse.user.registrationType) {
                console.log('RequireCompleteProfile: Profile complete after token refresh');
                hasCheckedRef.current = true;
                lastUserIdRef.current = user.id;
                setChecking(false);
                return;
              }
            }
          } catch (refreshErr) {
            console.error('RequireCompleteProfile: Token refresh failed:', refreshErr);
            // If refresh also returns 404, redirect to complete profile
            if (refreshErr?.response?.status === 404 || refreshErr?.message?.includes('not found')) {
              navigate('/complete-profile', { replace: true });
              return;
            }
          }
        }

        // For other errors, set error state instead of redirecting to prevent loops
        setError(errorMsg);
        setChecking(false);

      } finally {
        isCheckingRef.current = false;
      }
    };

    checkBackendProfile();
  }, [isLoaded, user?.id]); // Only depend on isLoaded and user.id to prevent loops

  // Show error state if profile check failed
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️ Profile verification failed</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => {
              hasCheckedRef.current = false;
              setChecking(true);
              setError(null);
            }}
            className="px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!isLoaded || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <LoadingSpinner size="xl" text="Checking profile..." />
      </div>
    );
  }

  return children;
};

export default RequireCompleteProfile;
